import type { ColdStartTier } from "@opus-dt/shared-types";

interface ProvenanceStripProps {
  entries: Array<{ tier: ColdStartTier }>;
}

// Ordem = hierarquia de evidência (espelha TIER_META do painel e o tierRank do backend). A cor
// desce junto com a evidência: verde onde há medição, âmbar onde há suposição.
const TIERS: Array<{ tier: ColdStartTier; label: string; fill: string }> = [
  { tier: "measured", label: "Histórico real", fill: "bg-rd-success-500" },
  { tier: "product", label: "Perfil por produto", fill: "bg-rd-info-500" },
  { tier: "similar", label: "Máquina semelhante", fill: "bg-rd-warning-500" },
  { tier: "spec", label: "Spec nominal", fill: "bg-text-tertiary" },
  { tier: "manual", label: "Premissa manual", fill: "bg-border-strong" },
];

/**
 * De onde vieram os dados da rodada inteira, numa barra.
 *
 * A lista por máquina responde "esta máquina tem histórico?"; ninguém somava as linhas para
 * responder "posso confiar nesta rodada?". Com poucas máquinas a lista basta; com vinte, a barra é
 * a única leitura viável — e é a pergunta que precede qualquer decisão sobre o resultado.
 */
export function ProvenanceStrip({ entries }: ProvenanceStripProps) {
  const total = entries.length;
  if (total === 0) return null;
  const counted = TIERS.map((t) => ({
    ...t,
    n: entries.filter((e) => e.tier === t.tier).length,
  })).filter((t) => t.n > 0);

  return (
    <div className="space-y-1.5">
      <div
        className="flex h-3 gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={counted.map((t) => `${t.n} de ${total}: ${t.label}`).join("; ")}
      >
        {counted.map((t) => (
          <div
            key={t.tier}
            className={`w-[var(--seg-w)] ${t.fill} first:rounded-l-full last:rounded-r-full`}
            style={{ "--seg-w": `${(t.n / total) * 100}%` } as React.CSSProperties}
          />
        ))}
      </div>
      {/* Legenda só quando há mais de uma origem: com uma só, ela repetiria palavra por palavra a
          frase que o painel já escreve abaixo — e duas vezes a mesma informação lê como ruído. */}
      {counted.length < 2 ? null : (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          {counted.map((t) => (
            <span key={t.tier} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 shrink-0 rounded-sm ${t.fill}`} aria-hidden />
              {t.label} · {t.n} de {total}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
